// Feature Requests (rebuilt 2026-09-27, founder "do A now").
//
// Before: a table of platforms and bare status dropdowns; on a phone the
// title scrolled off and nobody could see what had been started. Now:
// counts by status at the top (tap to filter), each row leads with the
// request and the member, the status carries who changed it and when, and
// "Decide" opens a panel above the table: status, the build it shipped
// in, an internal note, and one save. The server
// (utils/featureRequestDecision) owns the pipeline and the stamps.
// Members are not written back to (founder 2026-09-27: "we are not going
// to reply to members right now").
import { Icon } from "@iconify/react/dist/iconify.js";
import React, { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import SuccessAlert from "../child/Alert/SuccessAlert";
import DengerAlert from "../child/Alert/DengerAlert";
import moment from "moment";
import {
  getFeatureRequests,
  decideFeatureRequest,
  deleteFeatureRequest,
  deleteMultipleFeatureRequests,
} from "../../components/services/featureRequest";
import { getModulePermissions } from "../../utils/permissionUtils";
import { showConfirmationDialog } from "../../utils/apiHelper";

// Mirrors the server pipeline; the server list also sends `statuses`.
export const STATUSES = [
  { value: "new", label: "New" },
  { value: "under_review", label: "Reviewing" },
  { value: "planned", label: "Planned" },
  { value: "building", label: "Building" },
  { value: "released", label: "Shipped" },
  { value: "declined", label: "Not now" },
];
// Theme tokens only, so every pill reads in light and dark.
const STATUS_STYLE = {
  new: "badge bg-neutral-200 text-neutral-600",
  under_review: "badge bg-info-100 text-info-600",
  planned: "badge bg-warning-100 text-warning-700",
  building: "badge bg-primary-100 text-primary-600",
  released: "badge bg-success",
  declined: "badge bg-danger-subtle text-danger",
};
export const labelFor = (value, statuses = STATUSES) =>
  (statuses.find((s) => s.value === value) || {}).label || value || "N/A";
export const StatusPill = ({ value, statuses }) => (
  <span className={STATUS_STYLE[value] || STATUS_STYLE.new}>{labelFor(value, statuses)}</span>
);

const memberLine = (item) => {
  const m = item.member || {};
  const who = m.firstName || item.userId?.email || "Member";
  const city = m.city ? `, ${m.city}` : "";
  return `${who}${city}`;
};

const FeatureRequestListPage = () => {
  const [requests, setRequests] = useState([]);
  const [counts, setCounts] = useState({});
  const [statuses, setStatuses] = useState(STATUSES);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [selectedItems, setSelectedItems] = useState([]);
  const [successMessage, setSuccessMessage] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const [sortBy, setSortBy] = useState("createdAt");
  const [sortOrder, setSortOrder] = useState("desc");
  const [search, setSearch] = useState("");

  // Filters
  const [statusFilter, setStatusFilter] = useState("");
  const [platformFilter, setPlatformFilter] = useState("");

  // The decision panel, one request at a time.
  const [decide, setDecide] = useState(null);
  const [saving, setSaving] = useState(false);

  const navigate = useNavigate();
  const { shouldCheckPermissions, canEdit, canView, canDelete } =
    getModulePermissions("feature-requests");

  useEffect(() => {
    fetchData(currentPage);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    currentPage,
    itemsPerPage,
    statusFilter,
    platformFilter,
    search,
    sortBy,
    sortOrder,
  ]);

  const fetchData = async (page) => {
    try {
      // Ticks describe rows on screen; a reload of any kind starts clean.
      setSelectedItems([]);
      setLoading(true);
      const params = {
        page,
        limit: itemsPerPage,
        status: statusFilter,
        search,
        sortBy,
        sortOrder,
      };
      if (platformFilter) params.platform = platformFilter;

      const res = await getFeatureRequests(params);

      setRequests(res.data.data || []);
      setCounts(res.data.counts || {});
      if (Array.isArray(res.data.statuses) && res.data.statuses.length) setStatuses(res.data.statuses);
      setTotalItems(res.data.total || 0);
      setTotalPages(Math.ceil((res.data.total || 0) / itemsPerPage));
    } catch (err) {
      console.error(err);
      setErrorMessage(err.message || "Failed to fetch feature requests");
      setTimeout(() => setErrorMessage(""), 3000);
    } finally {
      setLoading(false);
    }
  };

  // Checkbox logic
  const handleSelectAll = (e) => {
    if (e.target.checked) setSelectedItems(requests.map((r) => r._id));
    else setSelectedItems([]);
  };

  const handleSelectItem = (id) => {
    setSelectedItems((prev) =>
      prev.includes(id) ? prev.filter((i) => i !== id) : [...prev, id]
    );
  };

  const isSelected = (id) => selectedItems.includes(id);

  // Pagination
  const handleNext = () =>
    currentPage < totalPages && setCurrentPage((p) => p + 1);
  const handlePrev = () =>
    currentPage > 1 && setCurrentPage((p) => p - 1);

  const getPaginationRange = () => {
    let start = Math.max(1, currentPage - 2);
    let end = Math.min(totalPages, start + 4);
    if (end - start < 4) start = Math.max(1, end - 4);
    return Array.from({ length: end - start + 1 }, (_, i) => start + i);
  };

  const toggleDateSort = () => {
    setSortBy("createdAt");
    setSortOrder(sortBy === "createdAt" && sortOrder === "desc" ? "asc" : "desc");
  };

  // ---------------------------------------------------------------- decide
  const openDecide = (item) => {
    if (decide && decide.id === item._id) return setDecide(null);
    setDecide({
      id: item._id,
      item,
      status: item.status,
      internalNote: item.internalNote || "",
      shippedIn: item.shippedIn || "",
    });
    try {
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch {}
  };

  const saveDecision = async () => {
    if (!decide) return;
    const { id, status, internalNote, shippedIn } = decide;
    try {
      setSaving(true);
      // The build belongs to Shipped alone.
      await decideFeatureRequest(id, { status, internalNote, shippedIn: status === "released" ? shippedIn : "" });
      setSuccessMessage("Saved.");
      setDecide(null);
      fetchData(currentPage);
    } catch (err) {
      setErrorMessage(err?.response?.data?.message || err.message || "Could not save");
    } finally {
      setSaving(false);
      setTimeout(() => {
        setSuccessMessage("");
        setErrorMessage("");
      }, 5000);
    }
  };

  const handleDelete = async (id, single = "No") => {
    const idsToDelete = single === "Yes" ? [id] : selectedItems;

    if (!idsToDelete || idsToDelete.length === 0) return;

    if (idsToDelete.length === 1) {
      single = "Yes";
    }

    const message =
      idsToDelete.length === 1
        ? "This removes the request from the list. The member is not told."
        : `This removes ${idsToDelete.length} requests from the list. The members are not told.`;
    const confirmmessage = "Yes, remove";

    const result = await showConfirmationDialog(message, confirmmessage);
    if (!result.isConfirmed) return;

    try {
      if (single === "Yes") {
        await deleteFeatureRequest(idsToDelete[0]);
      } else {
        await deleteMultipleFeatureRequests(idsToDelete);
      }
      setSelectedItems([]);
      setSuccessMessage(idsToDelete.length === 1 ? "Request removed." : "Requests removed.");
      setTimeout(() => setSuccessMessage(""), 3000);

      fetchData(currentPage);
    } catch (error) {
      setErrorMessage(
        error?.response?.data?.message || "Delete failed"
      );
      setTimeout(() => setErrorMessage(""), 3000);
    }
  };

  const capitalize = (str) => (str ? str.charAt(0).toUpperCase() + str.slice(1) : "N/A");
  const platformLabel = (p) => (p === "ios" ? "iOS" : capitalize(p));

  const chip = (value, label) => {
    const n = value === "" ? counts.all : counts[value];
    const active = statusFilter === value;
    return (
      <button
        key={value || "all"}
        type="button"
        className={`btn btn-sm radius-8 ${active ? "btn-primary" : "btn-outline-neutral-600"}`}
        onClick={() => {
          setStatusFilter(value);
          setCurrentPage(1);
        }}
        aria-pressed={active}
      >
        {label} <span className="fw-semibold">{n == null ? "" : n}</span>
      </button>
    );
  };

  const canDecide = !shouldCheckPermissions || canEdit;
  const showTicks = !shouldCheckPermissions || canDelete;
  const columnCount = 3 + (showTicks ? 1 : 0);

  return (
    <div className="card h-100 p-0 radius-12">
      {successMessage && (
        <SuccessAlert message={successMessage} onClose={() => setSuccessMessage("")} />
      )}
      {errorMessage && (
        <DengerAlert message={errorMessage} onClose={() => setErrorMessage("")} />
      )}

      {/* Counts by status: the one-glance answer to "what has nobody looked at" */}
      <div className="card-header border-bottom bg-base py-16 px-24 d-flex align-items-center flex-wrap gap-2">
        {chip("", "All")}
        {statuses.map((s) => chip(s.value, s.label))}
      </div>

      {/* Header Filters */}
      <div className="card-header border-bottom bg-base py-16 px-24 d-flex align-items-center flex-wrap gap-3 justify-content-between">
        <div className="d-flex align-items-center flex-wrap gap-3">
          <span className="text-md fw-medium text-secondary-light mb-0">
            Show
          </span>
          <select
            className="form-select form-select-sm w-auto ps-12 py-6"
            style={{
              fontSize: "16px",
              borderRadius: "7px",
              backgroundPositionX: "unset",
              backgroundPosition: "right 0.75rem center",
            }}
            value={itemsPerPage}
            onChange={(e) => {
              setItemsPerPage(parseInt(e.target.value, 10));
              setCurrentPage(1);
            }}
          >
            <option value={10}>10 items per page</option>
            <option value={20}>20 items per page</option>
            <option value={50}>50 items per page</option>
            <option value={100}>100 items per page</option>
          </select>

          {/* Search */}
          <form className="navbar-search" style={{ position: "relative" }} onSubmit={(e) => e.preventDefault()}>
            <input
              type="text"
              className="bg-base h-40-px w-auto pe-40"
              placeholder="Search titles"
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                setCurrentPage(1);
              }}
              style={{
                paddingRight: search ? "5rem" : "2.5rem",
                borderRadius: "7px",
              }}
            />
            <Icon
              icon="ion:search-outline"
              className="icon"
              style={{
                position: "absolute",
                right: search ? "3.25rem" : "1rem",
                top: "50%",
                transform: "translateY(-50%)",
                fontSize: "1.25rem",
                color: "#999",
                pointerEvents: "none",
              }}
            />
            {search && (
              <button
                type="button"
                onClick={() => {
                  setSearch("");
                  setCurrentPage(1);
                }}
                style={{
                  position: "absolute",
                  right: "1rem",
                  top: "30%",
                  transform: "translateY(-50%)",
                  fontSize: "1.25rem",
                  color: "#000",
                  background: "transparent",
                  border: "none",
                  cursor: "pointer",
                }}
              >
                <Icon icon="ic:round-close" />
              </button>
            )}
          </form>

          {/* Platform Filter */}
          <select
            className="form-select form-select-sm w-auto ps-12 py-6"
            style={{
              fontSize: "16px",
              borderRadius: "7px",
              backgroundPositionX: "unset",
              backgroundPosition: "right 0.75rem center",
            }}
            value={platformFilter}
            onChange={(e) => {
              setPlatformFilter(e.target.value);
              setCurrentPage(1);
            }}
          >
            <option value="">All Platforms</option>
            <option value="web">Web</option>
            <option value="ios">iOS</option>
            <option value="android">Android</option>
          </select>

          {/* Bulk remove only shows once something is ticked: a red button
              over a list of member ideas was an accident waiting to happen. */}
          {showTicks && selectedItems.length > 0 && (
            <button
              className="btn btn-sm btn-outline-danger radius-8"
              onClick={() => handleDelete(null, "No")}
            >
              Remove selected ({selectedItems.length})
            </button>
          )}
        </div>
      </div>

      <div className="card-body p-24">
        {/* The decision panel sits above the table so it fits a phone. */}
        {decide && (
          <div className="p-16 radius-8 mb-16" style={{ background: "rgba(140,80,253,0.06)", border: "1px solid var(--neutral-200)" }}>
            <div className="d-flex align-items-start justify-content-between gap-2 mb-2">
              <div>
                <div className="fw-semibold">{decide.item.title}</div>
                <div className="text-sm text-secondary-light">{memberLine(decide.item)}</div>
              </div>
              <StatusPill value={decide.item.status} statuses={statuses} />
            </div>
            <div className="row gy-3">
              <div className="col-md-3">
                <label className="form-label fw-semibold">Status</label>
                <select
                  className="form-select form-select-sm"
                  value={decide.status}
                  onChange={(e) => setDecide((d) => ({ ...d, status: e.target.value }))}
                >
                  {statuses.map((s) => (
                    <option key={s.value} value={s.value}>
                      {s.label}
                    </option>
                  ))}
                </select>
                {decide.status === "released" && (
                  <div className="mt-2">
                    <label className="form-label text-sm">Shipped in build</label>
                    <input
                      type="text"
                      className="form-control form-control-sm"
                      placeholder="2.4"
                      value={decide.shippedIn}
                      onChange={(e) => setDecide((d) => ({ ...d, shippedIn: e.target.value }))}
                      maxLength={20}
                    />
                  </div>
                )}
              </div>
              <div className="col-md-9">
                <label className="form-label fw-semibold">Internal note (staff only)</label>
                <textarea
                  className="form-control form-control-sm"
                  rows={3}
                  maxLength={2000}
                  placeholder="Why, who owns it, what it depends on."
                  value={decide.internalNote}
                  onChange={(e) => setDecide((d) => ({ ...d, internalNote: e.target.value }))}
                />
              </div>
              <div className="col-12 d-flex gap-2 justify-content-end">
                <button type="button" className="btn btn-sm btn-outline-neutral-600 radius-8" onClick={() => setDecide(null)} disabled={saving}>
                  Cancel
                </button>
                <button type="button" className="btn btn-sm btn-primary radius-8" onClick={saveDecision} disabled={saving}>
                  {saving ? "Saving" : "Save"}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Table */}
        <div className="table-responsive scroll-sm">
          <table className="table bordered-table sm-table mb-0" style={{ minWidth: 0 }}>
            <thead>
              <tr>
                {showTicks && (
                  <th scope="col" style={{ width: 40 }}>
                    <div className="form-check style-check d-flex align-items-center">
                      <input
                        type="checkbox"
                        name="checkbox"
                        className="form-check-input radius-4 border input-form-dark"
                        checked={requests.length > 0 && selectedItems.length === requests.length}
                        onClick={(e) => e.stopPropagation()}
                        onChange={handleSelectAll}
                      />
                    </div>
                  </th>
                )}
                <th onClick={toggleDateSort} style={{ cursor: "pointer" }} title="Newest or oldest first">
                  <div className="d-flex align-items-center gap-2">
                    Request
                    <span className="sort-colume">
                      <Icon
                        icon="mdi:arrow-up"
                        className={`arrow ${sortBy === "createdAt" && sortOrder === "asc" ? "active" : ""}`}
                      />
                      <Icon
                        icon="mdi:arrow-down"
                        className={`arrow ${sortBy === "createdAt" && sortOrder === "desc" ? "active" : ""}`}
                      />
                    </span>
                  </div>
                </th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={columnCount} className="text-center">
                    <span className="loader-15"></span>
                  </td>
                </tr>
              ) : requests.length > 0 ? (
                requests.map((item) => (
                  <tr key={item._id} className={decide && decide.id === item._id ? "bg-primary-50" : ""}>
                    {showTicks && (
                      <td>
                        <div className="form-check">
                          <input
                            type="checkbox"
                            className="form-check-input"
                            checked={isSelected(item._id)}
                            onChange={() => handleSelectItem(item._id)}
                          />
                        </div>
                      </td>
                    )}
                    <td style={{ minWidth: 140, whiteSpace: "normal" }}>
                      <div className="fw-semibold">{item.title || "N/A"}</div>
                      {item.firstLine && (
                        <div className="text-secondary-light text-sm">{item.firstLine}</div>
                      )}
                      <div className="text-sm mt-1">
                        {memberLine(item)}
                        <span className="text-secondary-light"> asked {moment(item.createdAt).fromNow()}</span>
                      </div>
                      <div className="text-xs text-secondary-light">
                        {platformLabel(item.platform)}
                        {item.appVersion ? ` ${item.appVersion}` : ""}
                      </div>
                    </td>
                    <td style={{ minWidth: 120, whiteSpace: "normal" }}>
                      <StatusPill value={item.status} statuses={statuses} />
                      <div className="text-xs text-secondary-light mt-1">{item.stamp}</div>
                      {item.status === "released" && item.shippedIn && (
                        <div className="text-xs text-secondary-light">Build {item.shippedIn}</div>
                      )}
                    </td>
                    <td>
                      <div className="d-flex flex-wrap gap-2 align-items-center">
                        {canDecide && (
                          <button
                            type="button"
                            className={`btn btn-sm radius-8 ${decide && decide.id === item._id ? "btn-primary" : "btn-outline-primary"}`}
                            onClick={() => openDecide(item)}
                          >
                            Decide
                          </button>
                        )}
                        {(!shouldCheckPermissions || canView) && (
                          <button
                            title="View"
                            onClick={() => navigate(`/feature-request/view/${item._id}`)}
                            className="bg-info-focus bg-hover-info-200 text-info-600 fw-medium w-40-px h-40-px d-flex justify-content-center align-items-center rounded-circle"
                          >
                            <Icon icon="majesticons:eye-line" className="icon text-xl" />
                          </button>
                        )}
                        {(!shouldCheckPermissions || canEdit) && (
                          <button
                            title="Edit the text"
                            onClick={() => navigate(`/feature-request/edit/${item._id}`)}
                            className="bg-success-focus text-success-600 bg-hover-success-200 fw-medium w-40-px h-40-px d-flex justify-content-center align-items-center rounded-circle"
                          >
                            <Icon icon="lucide:edit" className="menu-icon" />
                          </button>
                        )}
                        {showTicks && (
                          <button
                            title="Remove"
                            type="button"
                            onClick={() => handleDelete(item._id, "Yes")}
                            className="remove-item-btn bg-danger-focus bg-hover-danger-200 text-danger-600 fw-medium w-40-px h-40-px d-flex justify-content-center align-items-center rounded-circle"
                          >
                            <Icon icon="fluent:delete-24-regular" className="menu-icon" />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={columnCount} className="text-center">
                    No requests here.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        <div className="d-flex align-items-center justify-content-between flex-wrap gap-2 mt-24">
          <span>
            Showing {totalItems === 0 ? 0 : (currentPage - 1) * itemsPerPage + 1} to{" "}
            {Math.min(currentPage * itemsPerPage, totalItems)} of {totalItems}{" "}
            entries
          </span>
          <ul className="pagination d-flex flex-wrap align-items-center gap-2 justify-content-center">
            <li className={`page-item ${currentPage === 1 ? "disabled" : ""}`}>
              <Link
                className="page-link bg-neutral-200 text-secondary-light fw-semibold radius-8 border-0 d-flex align-items-center justify-content-center h-32-px text-md"
                to="#"
                onClick={handlePrev}
              >
                <Icon icon="ep:d-arrow-left" />
              </Link>
            </li>

            {getPaginationRange().map((page) => (
              <li key={page} className="page-item">
                <Link
                  className={`page-link text-secondary-light fw-semibold radius-8 border-0 d-flex align-items-center justify-content-center h-32-px w-32-px text-md ${currentPage === page
                    ? "bg-primary-600 text-white"
                    : "bg-neutral-200"
                    }`}
                  to="#"
                  onClick={() => setCurrentPage(page)}
                >
                  {page}
                </Link>
              </li>
            ))}

            <li
              className={`page-item ${currentPage === totalPages ? "disabled" : ""
                }`}
            >
              <Link
                className="page-link bg-neutral-200 text-secondary-light fw-semibold radius-8 border-0 d-flex align-items-center justify-content-center h-32-px text-md"
                to="#"
                onClick={handleNext}
              >
                <Icon icon="ep:d-arrow-right" />
              </Link>
            </li>
          </ul>
        </div>
      </div>
    </div>
  );
};

export default FeatureRequestListPage;
